// order-list.component.ts
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { OrderService } from './order-service';
import { OrderFlowService } from '../order-flow/order-flow.service';

import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';
import { Router, ActivatedRoute } from '@angular/router';
import { PatchOrderStatus, OrderStatus } from '../../model/order.model';
import { MultiselectDropdownComponent } from '../../shared/multiselect-dropdown/multiselect-dropdown.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-order-list',
  standalone: true,
  imports: [FormsModule, MultiselectDropdownComponent, CommonModule],
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.css']
})
export class OrderListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('orderSummaryModal') orderSummaryModal!: TemplateRef<any>;
  
  orders: any[] = [];
  searchTerm = '';
  selectedBatchAction = 'Batch action';
  countryCode = 'en-US';

  isLoading = false;
  selectedOrder: any;
  modalRef!: NgbModalRef;
  orderSummaryUrl: any;

  // Pagination properties
  pageNumber = 1;
  pageSize = 10;
  totalRows = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  readonly STATUS = {
    SAVED: 'Saved',
    SUBMITTED: 'Submitted',
    ORDERED: 'Ordered',
    CANCELLED: 'Cancelled',
    DISPATCHED: 'Despatched',
    TO_QUOTE: 'ToQuote',
    QUOTE: 'Quote',
    QUOTED: 'Quoted',
    READY_TO_SUBMIT: 'ReadyToSubmit',
  };

  patchOrderStatus: PatchOrderStatus | null = null;
  dropdownOptions: string[] = [];
  dropDownSelectedOptions: string[] = [];

  public Math = Math;

  constructor(
    private orderFlowService: OrderFlowService,
    private orderService: OrderService,
    private router: Router,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const code = params.get('countryCode');
      if (code) {
        this.countryCode = code;
      } else {
        const parentParams = this.route.parent?.snapshot.paramMap;
        const parentCode = parentParams?.get('countryCode');
        if (parentCode) {
          this.countryCode = parentCode;
        }
      }

      // Load initial orders
      this.loadOrders();
    });
  }

  ngAfterViewInit(): void {
    // Nothing needed here now, as we load orders in ngOnInit or on pagination change
  }

  ngOnDestroy(): void {
    // Clean up if needed
  }

  loadOrders(): void {
    // Include pagination params
    this.orderService.getOrdersWithPaging(this.searchTerm, this.dropDownSelectedOptions, this.pageNumber, this.pageSize).subscribe(
      data => {
        this.orders = data.data.items;
        this.pageNumber = data.data.pageNumber;
        this.pageSize = data.data.pageSize;
        this.totalRows = data.data.totalRows;
        this.totalPages = data.data.totalPages;
        this.hasNextPage = data.data.hasNextPage;
        this.hasPreviousPage = data.data.hasPreviousPage;

        this.dropdownOptions = Object.values(OrderStatus)
          .filter(value => typeof value === 'string')
          .map(value => value.toString());
        this.changeDetectorRef.detectChanges();
      },
      error => {
        console.error('Error fetching orders:', error);
      }
    );
  }

  onSearch(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const term = inputElement.value.trim();
    this.searchTerm = term;
    // Reset to first page whenever a new search is performed
    this.pageNumber = 1;
    this.loadOrders();
  }

  onSelectionChange(selected: string[]) {
    this.dropDownSelectedOptions = selected;
    this.pageNumber = 1; // Reset to the first page for new filters
    this.loadOrders();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.pageNumber = page;
    this.loadOrders();
  }

  nextPage(): void {
    if (this.hasNextPage) {
      this.pageNumber++;
      this.loadOrders();
    }
  }

  previousPage(): void {
    if (this.hasPreviousPage) {
      this.pageNumber--;
      this.loadOrders();
    }
  }

  exportList(): void {
    this.orderService.exportOrders(this.searchTerm, this.dropDownSelectedOptions).subscribe(
      response => {
        const blob = response.blob;
        const filename = response.filename;
        saveAs(blob, filename);
      },
      error => console.error('Error exporting orders:', error)
    );
  }

confirmSubmit(order: any): void {
    this.performChangeStatus(order, OrderStatus.Submitted);
  }

  editOrder(order: any): void {
    this.router.navigate(['/', this.countryCode, 'order-creation', 'edit', order.orderNumber], { queryParams: { status: order.status } });
  }

  duplicateOrder(order: any): void {
    this.router.navigate(['/', this.countryCode, 'order-creation', 'duplicate', order.orderNumber]);
  }

  cancelOrder(order: any, content: any): void {
    this.selectedOrder = order;
    this.modalRef = this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' });
  }

  confirmCancel(): void {
    this.performChangeStatus(this.selectedOrder, OrderStatus.Cancelled);
  }

  performChangeStatus(order: any, status: OrderStatus): void {
    this.patchOrderStatus = {
      orderNumber: order.orderNumber,
      orderStatus: OrderStatus[status]
    }

    this.isLoading = true;
    this.orderService.changeStatus(this.patchOrderStatus).subscribe(
      () => {
        this.loadOrders();
        this.isLoading = false;
        if (this.modalRef) {
          this.modalRef.dismiss();
        }
      },
      error => {
        console.error('Error cancelling order:', error);
        this.isLoading = false;
      }
    );
  }

  dismissCancel(): void {
    this.modalRef.dismiss();
  }

  toggleSelectAll(event: any): void {
    const isChecked = event.target.checked;
    this.orders.forEach(order => (order.selected = isChecked));
  }

  applyBatchAction(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    this.selectedBatchAction = inputElement.value;

    const selectedOrders = this.orders.filter(order => order.selected);
    if (selectedOrders.length === 0) {
      alert('Please select at least one order.');
      return;
    }

    if (this.selectedBatchAction === 'Delete') {
      if (!confirm('Are you sure you want to delete the selected orders?')) {
        return;
      }

      const orderIds = selectedOrders.map(order => order.id);
      this.orderService.deleteOrders(orderIds).subscribe(
        () => {
          // After deletion, reload current page to reflect changes
          this.loadOrders();
        },
        error => {
          console.error('Error deleting orders:', error);
        }
      );
    } else if (this.selectedBatchAction === 'Export') {
      const orderIds = selectedOrders.map(order => order.id);
      this.orderService.exportSelectedOrders(orderIds).subscribe(
        blob => {
          saveAs(blob, 'selected_orders.xlsx');
        },
        error => {
          console.error('Error exporting selected orders:', error);
        }
      );
    }
    this.selectedBatchAction = 'Batch action';
  }

  canSubmit(status: string): boolean {
    // Partners (approvers) can submit ReadyToSubmit orders from therapists
    // Everyone can submit Quoted orders
    if (status === this.STATUS.QUOTED) return true;
    if (status === this.STATUS.READY_TO_SUBMIT) return !this.authService.isTherapist();
    return false;
  }

  canEdit(status: string): boolean {
    // Therapists cannot edit ReadyToSubmit orders — they've already "submitted"
    if (status === this.STATUS.READY_TO_SUBMIT) return false;
    return [
      this.STATUS.SAVED, this.STATUS.SUBMITTED, this.STATUS.TO_QUOTE,
      this.STATUS.QUOTE, this.STATUS.QUOTED, this.STATUS.ORDERED, this.STATUS.DISPATCHED
    ].includes(status);
  }

  canDuplicate(status: string): boolean {
    return [this.STATUS.SUBMITTED, this.STATUS.ORDERED, this.STATUS.DISPATCHED].includes(status);
  }

  canCancel(status: string): boolean {
    return [this.STATUS.SAVED, this.STATUS.SUBMITTED].includes(status);
  }

  openOrderSummary(order: any): void {
    this.isLoading = true;
    this.selectedOrder = order;
    const url = `/${this.countryCode}/order-summary/${order.id}`;
    this.orderSummaryUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.modalRef = this.modalService.open(this.orderSummaryModal, {
      size: 'xl',
      windowClass: 'custom-modal-size',
      backdrop: 'static',
      keyboard: false
    });
    
    setTimeout(() => {
      const iframe = document.querySelector('iframe.order-summary-iframe');
      if (iframe) {
        iframe.addEventListener('load', () => {
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        });
      } else {
        
        setTimeout(() => {
          this.isLoading = false;
          this.changeDetectorRef.detectChanges();
        }, 1000);
      }
    }, 0);
  }

downloadQuotation(){
    this.callDownloadQuotation('msrp');
    this.callDownloadQuotation('dealer');
}

callDownloadQuotation(type: string) {
  this.isLoading = true;
  this.orderFlowService.quoteOrder(null, this.selectedOrder.orderNumber, type).subscribe({
    next: (res) => {
      try {
        const blob = (res as any).body ?? res;
        const contentDisposition =
          (res as any).headers?.get?.('content-disposition') ?? '';

        const rfc5987 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
        const basic = /filename=([^;]+)/i.exec(contentDisposition);
        
        let fileName: string;
        if (rfc5987?.[1]) {
          fileName = decodeURIComponent(rfc5987[1]);
        } else if (basic?.[1]) {
          fileName = basic[1].replace(/"/g, '');
        } else {
          fileName = `Quote_${type}_${this.selectedOrder.orderNumber ?? 'download'}.pdf`;
        }

        const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        alert('Error processing the quote. Please try again.');
      } finally {
        this.isLoading = false;
      }
    },
    error: () => {
      alert('Error creating the quote. Please try again.');
      this.isLoading = false;
    }
  });
}


  dismissOrderSummary(): void {
    if (this.modalRef) {
      this.modalRef.dismiss();
    }
  }

  trackByOrder(index: number, order: any): number {
    return order.id;
  }

  getRecordStart(): number {
    return (this.pageNumber - 1) * this.pageSize + 1;
  }
  
  getRecordEnd(): number {
    return Math.min(this.pageNumber * this.pageSize, this.totalRows);
  }

  getPageNumbers(): number[] {
    const maxVisiblePages = 5;
    const half = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, this.pageNumber - half);
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

}
